import { useEffect } from "react";
import { ArrowRight, Check } from "lucide-react";
import { trackOutboundClick, trackPageView } from "@/lib/track";

const TALLY = "https://tally.so/r/kd00gr";
const SAMPLE = "/sample-taskpilot-ai-mini-audit.pdf";
const EMAIL = "launchcheckstudio@proton.me";
const MAILTO = `mailto:${EMAIL}`;


const reportItems = [
  "Short QA summary",
  "Prioritized issue list",
  "Severity ratings",
  "Suggested fixes",
  "Retest checklist",
  "Optional AI-output notes",
];

const audienceCards: { title: string; body: string }[] = [
  {
    title: "You built the app yourself",
    body: "Which means you already know how it's supposed to work. That makes it easier to miss where a new user gets confused.",
  },
  {
    title: "You're launching soon",
    body: "Product Hunt, beta users, client demos, investor previews, paid traffic, whatever. Once people start clicking, small issues get loud fast.",
  },
  {
    title: "Your app uses AI",
    body: "AI features fail in weird ways. The happy path is not enough. The review looks for weak outputs, bad fallbacks, and confusing behavior.",
  },
  {
    title: "You don't need a giant QA engagement",
    body: "You need a focused pass on the parts most likely to embarrass you at launch.",
  },
];

const lookForCards: { title: string; body: string }[] = [
  {
    title: "Broken paths",
    body: "A button, form, page, or flow does not do what the user expects.",
  },
  {
    title: "Confusing moments",
    body: "The app leaves the user unsure what happened, what to do next, or whether something worked.",
  },
  {
    title: "Mobile problems",
    body: "The app technically loads on a phone, but buttons, forms, spacing, or navigation make it harder to use.",
  },
  {
    title: "AI behavior issues",
    body: "The AI gives weak, repetitive, poorly formatted, or unhelpful responses when the user input is imperfect.",
  },
  {
    title: "Launch-day friction",
    body: "Small issues that are easy to miss while building but obvious to someone using the app cold.",
  },
];

const howItWorks: { title: string; body: string }[] = [
  {
    title: "Submit your app or flow",
    body: "Send the app link, project context, what you want checked, and anything I should avoid touching.",
  },
  {
    title: "Scope gets confirmed",
    body: "I review the request first. If anything is unclear, I'll ask before starting. No mystery scope creep. No guessing.",
  },
  {
    title: "I run the mini-audit",
    body: "I check the agreed flow or feature for broken paths, confusing states, mobile issues, missing confirmations, weak AI behavior, or user-facing launch friction.",
  },
  {
    title: "You get the report",
    body: "You receive a short report with findings, severity ratings, suggested fixes, and a retest checklist.",
  },
];

const faqs: { q: string; a: string }[] = [
  {
    q: "Can't I just use the starter checklist myself?",
    a: "Yes, and you should. The checklist is useful for a first pass. The mini-audit is for when you want an outside reviewer to run the flow cold, catch what you missed, and turn the issues into a prioritized report instead of another vague to-do list.",
  },
  {
    q: "Why only one focused flow or feature?",
    a: "Because shallow reviews of an entire app are usually useless. A focused mini-audit goes deeper on the part that matters most: the signup flow, checkout flow, AI feature, onboarding path, or another launch-critical area.",
  },
  {
    q: "How long does a mini-audit take?",
    a: "Typical turnaround is 48 hours after scope, access, and context are confirmed. If the request is unclear or needs extra info, I'll ask before starting.",
  },
  {
    q: "What do I need to provide?",
    a: "A live link, demo link, staging link, screenshots, or walkthrough. If login is required, provide a safe temporary test account with limited access. Do not send personal passwords or sensitive credentials.",
  },
  {
    q: "What do you actually review?",
    a: "One focused flow, feature, or launch-critical area. Common examples include signup, login/reset, mobile layout, forms, buttons, confirmation messages, empty states, checkout flow, or AI-output behavior.",
  },
  {
    q: "Do you test payments?",
    a: "Only with a safe test or sandbox checkout flow. I will not complete real purchases or trigger real charges unless that is explicitly discussed and approved.",
  },
  {
    q: "Do you make the fixes?",
    a: "No. The mini-audit is a review and recommendation pass. You get findings, severity ratings, suggested fixes, and a retest checklist. Implementation would be separate.",
  },
  {
    q: "Do you retest after I make changes?",
    a: "A focused retest of the original findings can be added after delivery. It only checks whether the reported issues were fixed.",
  },
  {
    q: "Is this a security audit or compliance review?",
    a: "No. This is not a security audit, legal compliance review, accessibility certification, or full enterprise QA engagement. It is a focused pre-launch review for catching obvious user-flow problems before real users do.",
  },
  {
    q: "Can you work under an NDA?",
    a: "For early mini-audits, keep the submission limited to what is safe to share. If an NDA is required, mention it in the intake form before sending private details.",
  },
  {
    q: "What if my request is too big for a mini-audit?",
    a: "I'll say so before starting. If it's better handled as a larger workflow review, implementation project, or specialist engagement, I won't try to cram it into a mini-audit and pretend that's useful.",
  },
];

type CTAProps = { href: string; label: string; children: React.ReactNode; title?: string };

const handleTrack = (href: string, label: string) => {
  void trackOutboundClick(href, label);
};

const PrimaryButton = ({ href, label, children, title }: CTAProps) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => handleTrack(href, label)}
    onAuxClick={() => handleTrack(href, label)}
    data-track-label={label}
    title={title}
    aria-label={title ?? undefined}
    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand px-6 py-3.5 text-base font-semibold text-brand-foreground shadow-sm transition hover:opacity-90 hover:translate-y-[-1px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:w-auto sm:py-3"
  >
    {children}
    <ArrowRight className="h-4 w-4" />
  </a>
);

const SecondaryButton = ({ href, label, children, title }: CTAProps) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => handleTrack(href, label)}
    onAuxClick={() => handleTrack(href, label)}
    data-track-label={label}
    title={title}
    aria-label={title ? `${typeof children === "string" ? children : ""} — ${title}`.trim() : undefined}
    className="inline-flex w-full items-center justify-center gap-2 rounded-md border-2 border-foreground/30 bg-background px-6 py-3.5 text-base font-semibold text-foreground transition hover:border-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:w-auto sm:py-3"
  >
    {children}
  </a>
);

const Section = ({
  eyebrow,
  title,
  children,
  className = "",
  id,
}: {
  eyebrow?: string;
  title: string;
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => (
  <section id={id} className={`border-t border-border/60 py-20 sm:py-24 ${className}`}>
    <div className="mx-auto max-w-5xl px-6">
      {eyebrow && (
        <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {eyebrow}
        </div>
      )}
      <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
      <div className="mt-10">{children}</div>
    </div>
  </section>
);

const EmailFirstNote = () => (
  <p className="mt-4 text-center text-sm text-muted-foreground">
    Not sure if your request fits? Email first:{" "}
    <a
      href={MAILTO}
      onClick={() => handleTrack(MAILTO, "Email first")}
      className="font-medium text-foreground underline underline-offset-4 hover:text-brand"
    >
      {EMAIL}
    </a>
  </p>
);

const Index = () => {
  useEffect(() => {
    type IdleWindow = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
    };
    const w = window as IdleWindow;
    const run = () => {
      void trackPageView();
    };
    // Defer analytics until AFTER the load event so it stays off the
    // critical request chain that Lighthouse measures for performance.
    const schedule = () => {
      if (typeof w.requestIdleCallback === "function") {
        w.requestIdleCallback(run, { timeout: 4000 });
      } else {
        setTimeout(run, 2500);
      }
    };
    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/60">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
          <a
            href="#top"
            className="flex items-center gap-2 font-bold tracking-tight rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-brand" />
            LaunchCheck Studio
          </a>
          {/* Desktop link */}
          <a
            href={TALLY}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleTrack(TALLY, "Header — Request a Mini-Audit")}
            onAuxClick={() => handleTrack(TALLY, "Header — Request a Mini-Audit")}
            className="hidden rounded-sm text-sm font-semibold text-foreground hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:inline"
          >
            Request a Mini-Audit →
          </a>
          {/* Compact mobile button */}
          <a
            href={TALLY}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleTrack(TALLY, "Header Mobile — Request")}
            onAuxClick={() => handleTrack(TALLY, "Header Mobile — Request")}
            className="inline-flex items-center justify-center rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-brand-foreground shadow-sm transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:hidden"
          >
            Request
          </a>
        </div>
      </header>

      {/* Hero */}
      <main id="top">
        <section className="px-6 pt-10 pb-14 sm:pt-28 sm:pb-24">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-4 inline-flex items-center rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground sm:mb-5">
              App launch review for small builders
            </div>
            <h1 className="text-3xl font-bold leading-[1.1] tracking-tight sm:text-6xl sm:leading-[1.05]">
              I test your app <span className="text-brand">before real users do.</span>
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground sm:mt-6 sm:text-xl">
              Send your app, demo, or workflow. I click through it like a first-time user, find the
              confusing or broken parts, and send back a short report showing what to fix first.
            </p>
            <div className="mt-6 flex flex-col items-stretch justify-center gap-3 sm:mt-9 sm:flex-row sm:items-center">
              <PrimaryButton href={TALLY} label="Hero — Request a Mini-Audit">Request a Mini-Audit</PrimaryButton>
              <SecondaryButton href={SAMPLE} label="Hero — View Example Report" title="Opens an example report PDF in a new tab">View Example Report</SecondaryButton>
            </div>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              $49 starter rate · 48-hour turnaround after scope is confirmed · one focused flow or feature
            </p>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              You'll submit a short form first. I'll confirm the scope before any payment or review starts.
            </p>
            <p className="mt-2 text-center text-xs text-muted-foreground">Example report PDF · opens in new tab</p>
            <EmailFirstNote />
          </div>
        </section>

        {/* What this is */}
        <Section eyebrow="What this is" title="A second set of eyes before you launch.">
          <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground">
            LaunchCheck Studio is a focused app review service. You send the app, demo, or workflow
            you're about to share with users. I go through it like someone seeing it for the first
            time and flag the parts that feel broken, confusing, unfinished, or risky before launch.
          </p>
        </Section>

        {/* Pricing / Scope */}
        <Section eyebrow="Starter Rate" title="A focused review for $49.">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-6 sm:p-8 lg:col-span-2">
              <p className="text-base leading-relaxed text-foreground/80 sm:text-lg">
                For early reviews, the LaunchCheck starter rate is{" "}
                <span className="font-semibold text-foreground">$49</span>.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                You're paying for a fresh set of eyes on one important part of your app, not a
                generic checklist. I'll review the agreed flow or feature, identify the issues most
                likely to confuse users or block progress, and send back a short report showing what
                to fix first.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Typical turnaround is{" "}
                <span className="font-semibold text-foreground">48 hours</span> after scope, access,
                and context are confirmed. If the request is bigger than a focused review, I'll say
                so before any payment or review starts.
              </p>
            </div>
            <aside className="rounded-xl border border-border bg-secondary p-6 sm:p-8">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Scope note
              </div>
              <p className="mt-3 text-base leading-relaxed text-foreground/80">
                Best fit: one important flow, one AI feature, or one focused launch concern. Not a
                full app rebuild, security audit, or implementation project.
              </p>
            </aside>
          </div>
          <div className="mt-8 flex flex-col items-center justify-center gap-3">
            <PrimaryButton href={TALLY} label="Pricing — Request a Mini-Audit">Request a Mini-Audit</PrimaryButton>
            <p className="text-center text-sm text-muted-foreground">
              Scope is confirmed before payment or review starts.
            </p>
          </div>
        </Section>

        {/* How It Works */}
        <Section eyebrow="How It Works" title="Simple review. Clear next step.">
          <ol className="grid gap-4 sm:grid-cols-2">
            {howItWorks.map((step, i) => (
              <li
                key={step.title}
                className="rounded-lg border border-border bg-card p-5"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand text-sm font-bold text-brand-foreground">
                    {i + 1}
                  </span>
                  <h3 className="text-base font-semibold">{step.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
              </li>
            ))}
          </ol>
        </Section>

        {/* What I Look For */}
        <Section
          eyebrow="What I check for"
          title="The moments where users get stuck, confused, or lose trust."
        >
          <p className="max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            The goal is to find the points where a real user gets blocked, confused, or unsure what
            just happened.
          </p>
          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {lookForCards.map((card) => (
              <li
                key={card.title}
                className="rounded-lg border border-border bg-card p-5"
              >
                <h3 className="text-base font-semibold">{card.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
              </li>
            ))}
          </ul>
        </Section>

        {/* What You Receive — report preview */}
        <Section eyebrow="What you get back" title="A short report showing what to fix first.">
          <p className="max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            The report is built for action. Each finding explains what happened, why it matters,
            how serious it is, and what to check again after changes.
          </p>

          <div className="mt-8 grid gap-6 lg:grid-cols-5">
            {/* Mock report card */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8 lg:col-span-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Example finding
                </div>
                <span className="inline-flex items-center rounded-full bg-brand/10 px-2.5 py-0.5 text-xs font-semibold text-[hsl(14_88%_32%)]">
                  High severity
                </span>
              </div>
              <dl className="mt-5 space-y-4 text-sm sm:text-base">
                <div>
                  <dt className="font-semibold text-foreground">Issue</dt>
                  <dd className="mt-1 text-muted-foreground">
                    Password reset email does not arrive.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-foreground">Why it matters</dt>
                  <dd className="mt-1 text-muted-foreground">
                    Users may get locked out, creating avoidable support requests.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-foreground">Suggested fix</dt>
                  <dd className="mt-1 text-muted-foreground">
                    Confirm the reset email is triggered, delivered, and not blocked by spam
                    settings.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-foreground">Retest</dt>
                  <dd className="mt-1 text-muted-foreground">
                    Confirm the reset email arrives promptly and the reset link works.
                  </dd>
                </div>
              </dl>
            </div>

            {/* Deliverables list */}
            <ul className="grid gap-2 self-start lg:col-span-2">
              {reportItems.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3"
                >
                  <Check className="h-5 w-5 flex-shrink-0 text-brand" />
                  <span className="font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </Section>

        {/* Who This Is For */}
        <Section eyebrow="Who This Is For" title="Built for builders who need another set of eyes before traffic shows up.">
          <ul className="grid gap-4 sm:grid-cols-2">
            {audienceCards.map((card) => (
              <li
                key={card.title}
                className="rounded-lg border border-border bg-card p-5"
              >
                <h3 className="text-base font-semibold">{card.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
              </li>
            ))}
          </ul>
        </Section>

        {/* FAQ */}
        <Section
          eyebrow="FAQ"
          title="Questions before you hand over the app keys?"
          id="faq"
        >
          <div className="divide-y divide-border rounded-xl border border-border bg-card">
            {faqs.map((item) => (
              <details key={item.q} className="group p-5 sm:p-6">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-foreground">
                  <span>{item.q}</span>
                  <span
                    aria-hidden
                    className="mt-1 inline-block h-2 w-2 flex-shrink-0 rotate-45 border-b-2 border-r-2 border-foreground/60 transition-transform group-open:-rotate-135"
                  />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </Section>

        {/* Final CTA */}
        <section className="border-t border-border/60 px-6 py-24 sm:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-5xl">
              Ready for a second set of eyes?
            </h2>
            <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              <PrimaryButton href={TALLY} label="Final CTA — Request a Mini-Audit">Request a Mini-Audit</PrimaryButton>
              <SecondaryButton href={SAMPLE} label="Final CTA — View Example Report" title="Opens an example report PDF in a new tab">View Example Report</SecondaryButton>
            </div>
            <p className="mt-2 text-center text-xs text-muted-foreground">Example report PDF · opens in new tab</p>
            <EmailFirstNote />
          </div>
        </section>
      </main>

      <footer className="border-t border-border/60 px-6 py-12">
        <div className="mx-auto grid max-w-5xl gap-8 sm:grid-cols-3">
          <div>
            <div className="flex items-center gap-2 font-bold tracking-tight">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-brand" />
              LaunchCheck Studio
            </div>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Pre-launch QA mini-audits for no-code and AI apps.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Contact
            </div>
            <a
              href={MAILTO}
              onClick={() => handleTrack(MAILTO, "Footer — Email")}
              className="mt-2 inline-block text-sm font-medium text-foreground underline underline-offset-4 hover:text-brand"
            >
              {EMAIL}
            </a>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Links
            </div>
            <ul className="mt-2 space-y-1.5 text-sm">
              <li>
                <a
                  href={TALLY}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleTrack(TALLY, "Footer — Request")}
                  onAuxClick={() => handleTrack(TALLY, "Footer — Request")}
                  className="text-foreground hover:text-brand"
                >
                  Request a Mini-Audit
                </a>
              </li>
              <li>
                <a
                  href={SAMPLE}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Example report PDF — opens in new tab"
                  aria-label="View Example Report (PDF, opens in new tab)"
                  onClick={() => handleTrack(SAMPLE, "Footer — Sample")}
                  onAuxClick={() => handleTrack(SAMPLE, "Footer — Sample")}
                  className="text-foreground hover:text-brand"
                >
                  View Example Report{" "}
                  <span className="text-xs text-muted-foreground">(PDF)</span>
                </a>
              </li>
              <li>
                <a
                  href={MAILTO}
                  onClick={() => handleTrack(MAILTO, "Footer — Email First")}
                  className="text-foreground hover:text-brand"
                >
                  Email First
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mx-auto mt-10 max-w-5xl border-t border-border/60 pt-6 text-xs leading-relaxed text-muted-foreground">
          <p>
            Submitted information is used only to review and respond to your request. Do not send
            passwords, production credentials, private keys, or sensitive user data.
          </p>
          <p className="mt-3">© {new Date().getFullYear()} LaunchCheck Studio</p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
